// Microsoft 365 operations through Microsoft Graph, shared by VERA.exe and VERA Studio on the web.
// One copy of this logic: VERA.exe runs it in its main process (graph.ts), the web app runs it in the page (studio/src/lib/m365.ts).
// - Delegated only: VERA acts as the signed-in person and sees only what they can already see.
// - Only the operations listed here exist, and every input is checked.
// - Anything that creates something (a draft, a meeting, a Planner task) or sends email asks the person first through
//   `confirm`: a Windows confirmation in VERA.exe, a confirmation in the page on the web. VERA's model can never send by itself.
// - No Electron and no Node here, so the same file runs in both places.

export const GRAPH_SCOPES = {
  outlook: ["Mail.ReadWrite", "Calendars.ReadWrite", "Calendars.Read.Shared"],
  // Sending is separate, so Microsoft asks for it only the first time the person sends an email from Studio.
  mailsend: ["Mail.ReadWrite", "Mail.Send"],
  teams: ["Chat.Read", "Team.ReadBasic.All", "Channel.ReadBasic.All"],
  planner: ["Tasks.ReadWrite", "Group.Read.All"],
  files: ["Files.Read.All", "Sites.Read.All"],
} as const;
export type GraphKind = keyof typeof GRAPH_SCOPES;
export const GRAPH_KINDS = Object.keys(GRAPH_SCOPES) as GraphKind[];

export class GraphInputError extends Error {}

// ---------- links in emails ----------
export type LinkInfo = { text: string; url: string };
const MAX_LINKS = 40;
const decodeEntities = (s: string) => s
  .replace(/&amp;/gi, "&").replace(/&quot;/gi, '"').replace(/&#39;|&apos;/gi, "'").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">")
  .replace(/&nbsp;/gi, " ").replace(/&#(\d+);/g, (_m, n) => String.fromCodePoint(Number(n)));

/** The web links in an email's HTML, with the words on each button or link. */
export function extractLinks(html: string): LinkInfo[] {
  const out = new Map<string, LinkInfo>();
  const re = /<a\b[^>]*?\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a>/gi;
  for (let m = re.exec(html); m && out.size < MAX_LINKS; m = re.exec(html)) {
    const url = decodeEntities((m[1] ?? m[2] ?? m[3] ?? "").trim());
    if (!/^https?:\/\//i.test(url) || url.length > 2000) continue;
    const inner = m[4] ?? "";
    const alt = /\balt\s*=\s*"([^"]*)"/i.exec(inner)?.[1] ?? "";
    const text = decodeEntities(inner.replace(/<[^>]+>/g, " ")).replace(/\s+/g, " ").trim() || decodeEntities(alt).trim() || "(image link)";
    if (!out.has(url)) out.set(url, { text: text.slice(0, 120), url });
  }
  return [...out.values()];
}

// ---------- bytes (no Node Buffer, so this runs in a browser too) ----------
const base64ToText = (b64: string) => { const bin = atob(b64); const bytes = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i); return new TextDecoder().decode(bytes); };
const bytesToBase64 = (bytes: Uint8Array) => { let bin = ""; for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000)); return btoa(bin); };

const BASE = "https://graph.microsoft.com/v1.0";
const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

// ---------- input checks ----------
const str = (v: unknown, name: string, max = 500, min = 1) => {
  if (typeof v !== "string" || v.trim().length < min || v.length > max) throw new GraphInputError(`Please check the ${name}.`);
  return v.trim();
};
const optStr = (v: unknown, name: string, max = 5000) => (v === undefined || v === null || v === "" ? "" : str(v, name, max));
const id = (v: unknown) => encodeURIComponent(str(v, "item", 1024));
const EMAIL = /^[^\s@<>()"',;:]+@[^\s@<>()"',;:]+\.[^\s@<>()"',;:]+$/;
const emails = (v: unknown, max = 30) => {
  if (!Array.isArray(v) || v.length > max) throw new GraphInputError("Please check the email addresses.");
  return v.map((e) => { const s = str(e, "email address", 254); if (!EMAIL.test(s)) throw new GraphInputError(`"${s}" isn't an email address.`); return s; });
};
const minutes = (v: unknown) => { const n = Number(v); if (!Number.isInteger(n) || n < 5 || n > 480) throw new GraphInputError("Please check the length of the meeting."); return n; };
const localDateTime = (v: unknown) => { const s = str(v, "date and time", 25); if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s)) throw new GraphInputError("Please check the date and time."); return s.length === 16 ? `${s}:00` : s; };
const isoDate = (v: unknown, name: string) => { const s = str(v, name, 10); if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || Number.isNaN(new Date(`${s}T00:00:00`).getTime())) throw new GraphInputError(`Please check the ${name}.`); return s; };
const optEmails = (v: unknown, max = 30) => (v === undefined || v === null ? [] : emails(v, max));
const pad = (n: number) => String(n).padStart(2, "0");

// ---------- files on outgoing email ----------
export const MAX_SEND_FILE = 3 * 1024 * 1024;
export const MAX_READ_ATTACHMENT = 15 * 1024 * 1024;
type OutFile = { name: string; contentType: string; contentBytes: string; size: number };
/** Files to attach: [{ name, mimeType, dataUrl }], each at most 3 MB, at most 5. */
function outFiles(v: unknown): OutFile[] {
  if (v === undefined || v === null) return [];
  if (!Array.isArray(v) || v.length > 5) throw new GraphInputError("Please attach at most 5 files.");
  return v.map((f) => {
    const name = str((f as any)?.name, "file name", 255);
    const m = /^data:([^;,]*);base64,([A-Za-z0-9+/=]+)$/.exec(String((f as any)?.dataUrl ?? ""));
    if (!m) throw new GraphInputError(`${name} didn't come through. Please attach it again.`);
    const size = Math.floor((m[2].length * 3) / 4) - (m[2].endsWith("==") ? 2 : m[2].endsWith("=") ? 1 : 0);
    if (size > MAX_SEND_FILE) throw new GraphInputError(`${name} is larger than 3 MB, so it can't be attached here yet. Share it as a link instead.`);
    return { name, contentType: m[1] || "application/octet-stream", contentBytes: m[2], size };
  });
}
async function attachAll(call: Call, messageId: string, files: OutFile[]) {
  for (const f of files) {
    await call("POST", `/me/messages/${encodeURIComponent(messageId)}/attachments`, { "@odata.type": "#microsoft.graph.fileAttachment", name: f.name, contentType: f.contentType, contentBytes: f.contentBytes });
  }
}
/** Graph gives email times in UTC; VERA should see the person's own local time. */
export const localStamp = (iso?: string | null) => { if (!iso) return null; const d = new Date(iso); return Number.isNaN(d.getTime()) ? iso : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())} (${tz()})`; };
const recipients = (list: string[]) => list.map((address) => ({ emailAddress: { address } }));
const mailSummary = (m: any) => ({
  id: m.id, subject: m.subject ?? "(no subject)", from: person(m.from), to: (m.toRecipients ?? []).map((r: any) => person(r).email).filter(Boolean),
  received: localStamp(m.receivedDateTime), sent: localStamp(m.sentDateTime), preview: m.bodyPreview ?? "", hasAttachments: Boolean(m.hasAttachments), unread: m.isRead === false, webLink: m.webLink,
});
const toLocal = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;

export type Call = (method: "GET" | "POST" | "PATCH", path: string, body?: unknown, headers?: Record<string, string>) => Promise<any>;
export type Confirm = (message: string, detail: string) => Promise<boolean>;

export type Op = { kind: GraphKind; run: (args: Record<string, unknown>, call: Call, confirm: Confirm) => Promise<unknown> };

/** Teams message HTML as plain text. */
export const decodeHtmlText = (html: string) => html.replace(/<br\s*\/?>|<\/p>/gi, "\n").replace(/<[^>]+>/g, " ")
  .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'")
  .replace(/[ \t]+/g, " ").replace(/\s*\n\s*/g, "\n").trim();

const person = (r?: { emailAddress?: { name?: string; address?: string } }) => ({ name: r?.emailAddress?.name ?? r?.emailAddress?.address ?? "", email: r?.emailAddress?.address ?? "" });

export const OPS: Record<string, Op> = {
  // ----- Outlook -----
  "calendar.day": {
    kind: "outlook",
    async run(args, call) {
      const days = Math.min(7, Math.max(1, Number(args.days) || 1));
      const start = new Date(); start.setHours(0, 0, 0, 0);
      const end = new Date(start); end.setDate(end.getDate() + days);
      const q = `startDateTime=${encodeURIComponent(start.toISOString())}&endDateTime=${encodeURIComponent(end.toISOString())}&$select=subject,start,end,isOnlineMeeting,onlineMeeting,webLink,attendees,organizer,location&$orderby=start/dateTime&$top=30`;
      const r = await call("GET", `/me/calendarView?${q}`, undefined, { Prefer: `outlook.timezone="${tz()}"` });
      return (r.value ?? []).map((e: any) => ({
        id: e.id, subject: e.subject ?? "(no title)", start: e.start?.dateTime, end: e.end?.dateTime, webLink: e.webLink,
        teams: Boolean(e.isOnlineMeeting), joinUrl: e.onlineMeeting?.joinUrl ?? null, location: e.location?.displayName ?? "",
        organizer: person(e.organizer).name, attendees: (e.attendees ?? []).slice(0, 12).map((a: any) => person(a).name),
      }));
    },
  },
  "mail.inbox": {
    kind: "outlook",
    async run(_args, call) {
      const r = await call("GET", "/me/mailFolders/inbox/messages?$top=12&$select=id,subject,from,receivedDateTime,bodyPreview,isRead,webLink&$orderby=receivedDateTime desc");
      return (r.value ?? []).map((m: any) => ({ id: m.id, subject: m.subject ?? "(no subject)", from: person(m.from), received: m.receivedDateTime, preview: m.bodyPreview ?? "", unread: !m.isRead, webLink: m.webLink }));
    },
  },
  "mail.search": {
    kind: "outlook",
    async run(args, call) {
      const folder = args.folder === "sent" ? "/me/mailFolders/sentitems/messages" : args.folder === "all" ? "/me/messages" : "/me/mailFolders/inbox/messages";
      const top = Math.min(25, Math.max(1, Number(args.max) || 10));
      const q = optStr(args.query, "search", 200).replace(/["\\]/g, " ").trim();
      const select = "$select=id,subject,from,toRecipients,receivedDateTime,sentDateTime,bodyPreview,hasAttachments,isRead,webLink";
      const path = q ? `${folder}?$search=${encodeURIComponent(`"${q}"`)}&$top=${top}&${select}` : `${folder}?$top=${top}&$orderby=receivedDateTime desc&${select}`;
      const r = await call("GET", path);
      return (r.value ?? []).map(mailSummary);
    },
  },
  "mail.read": {
    kind: "outlook",
    async run(args, call) {
      const m = await call("GET", `/me/messages/${id(args.id)}?$select=subject,from,toRecipients,ccRecipients,receivedDateTime,sentDateTime,body,webLink,hasAttachments,conversationId&$expand=attachments($select=id,name,contentType,size,isInline)`, undefined, { Prefer: 'outlook.body-content-type="text"' });
      // The buttons and links in the email ("Track my luggage"), so VERA can open one when the person asks.
      let links: LinkInfo[] = [];
      try { links = extractLinks(String((await call("GET", `/me/messages/${id(args.id)}?$select=body`)).body?.content ?? "")); } catch { /* the text is enough */ }
      return {
        links,
        id: m.id, subject: m.subject ?? "", from: person(m.from), received: localStamp(m.receivedDateTime), sent: localStamp(m.sentDateTime),
        to: (m.toRecipients ?? []).map((r: any) => person(r)), cc: (m.ccRecipients ?? []).map((r: any) => person(r)),
        text: String(m.body?.content ?? "").slice(0, 100_000), webLink: m.webLink,
        attachments: (m.attachments ?? []).filter((a: any) => !a.isInline).map((a: any) => ({ id: a.id, name: a.name, contentType: a.contentType, size: a.size })),
      };
    },
  },
  "mail.attachment": {
    kind: "outlook",
    async run(args, call) {
      const a = await call("GET", `/me/messages/${id(args.messageId)}/attachments/${id(args.attachmentId)}`);
      if (a["@odata.type"] !== "#microsoft.graph.fileAttachment") return { name: a.name ?? "attachment", note: "This attachment is an Outlook item or a link, not a file VERA can open." };
      const size = Number(a.size) || 0;
      if (size > MAX_READ_ATTACHMENT) return { name: a.name, size, note: "This attachment is larger than 15 MB, so VERA can't open it here." };
      const contentType = String(a.contentType || "application/octet-stream");
      const isText = /^text\/|\/(json|csv|xml)$/.test(contentType) || /\.(txt|md|csv|json)$/i.test(String(a.name));
      return isText
        ? { name: a.name, contentType, size, text: base64ToText(String(a.contentBytes ?? "")).slice(0, 100_000) }
        : { name: a.name, contentType, size, dataUrl: `data:${contentType};base64,${a.contentBytes ?? ""}` };
    },
  },
  "mail.send": {
    kind: "mailsend",
    async run(args, call, confirm) {
      const subject = optStr(args.subject, "subject", 255);
      const body = str(args.body, "message", 50_000);
      const to = optEmails(args.to);
      const cc = optEmails(args.cc);
      const replyTo = args.replyToId ? id(args.replyToId) : "";
      const files = outFiles(args.attachments);
      if (!replyTo && !to.length) throw new GraphInputError("Who should this go to? Add at least one email address.");
      if (!replyTo && !subject) throw new GraphInputError("Please add a subject.");
      const lines = [
        replyTo ? `Reply${to.length ? ` to: ${to.join(", ")}` : " to the sender"}` : `To: ${to.join(", ")}`,
        ...(cc.length ? [`Cc: ${cc.join(", ")}`] : []),
        ...(subject && !replyTo ? [`Subject: ${subject}`] : []),
        ...(files.length ? [`Attached: ${files.map((f) => f.name).join(", ")}`] : []),
        "", body.length > 700 ? `${body.slice(0, 700)}...` : body, "",
        "It goes out from your mailbox as soon as you say Yes.",
      ];
      if (!(await confirm("Send this email now?", lines.join("\n")))) return { cancelled: true };
      let draftId: string;
      if (replyTo) {
        const d = await call("POST", `/me/messages/${replyTo}/createReply`, { comment: body });
        draftId = d.id;
        if (to.length || cc.length) await call("PATCH", `/me/messages/${encodeURIComponent(draftId)}`, { ...(to.length ? { toRecipients: recipients(to) } : {}), ...(cc.length ? { ccRecipients: recipients(cc) } : {}) });
      } else {
        const d = await call("POST", "/me/messages", { subject, body: { contentType: "Text", content: body }, toRecipients: recipients(to), ccRecipients: recipients(cc) });
        draftId = d.id;
      }
      await attachAll(call, draftId, files);
      await call("POST", `/me/messages/${encodeURIComponent(draftId)}/send`);
      return { sent: true };
    },
  },
  "mail.draft": {
    kind: "outlook",
    async run(args, call, confirm) {
      const subject = str(args.subject, "subject", 255);
      const body = str(args.body, "message", 50_000);
      const to = args.to ? emails(args.to) : [];
      const cc = optEmails(args.cc);
      const replyTo = args.replyToId ? id(args.replyToId) : "";
      const files = outFiles(args.attachments);
      if (!(await confirm("Save this draft in your Outlook?", `${replyTo ? "Reply" : `To: ${to.join(", ") || "(you'll add recipients)"}`}\nSubject: ${subject}${files.length ? `\nAttached: ${files.map((f) => f.name).join(", ")}` : ""}\n\nIt stays in your Drafts folder until you send it.`))) return { cancelled: true };
      let d: any;
      if (replyTo) {
        d = await call("POST", `/me/messages/${replyTo}/createReply`, { comment: body });
        if (to.length || cc.length) await call("PATCH", `/me/messages/${encodeURIComponent(d.id)}`, { ...(to.length ? { toRecipients: recipients(to) } : {}), ...(cc.length ? { ccRecipients: recipients(cc) } : {}) });
      } else {
        d = await call("POST", "/me/messages", { subject, body: { contentType: "Text", content: body }, toRecipients: recipients(to), ccRecipients: recipients(cc) });
      }
      await attachAll(call, d.id, files);
      return { webLink: d.webLink };
    },
  },
  "calendar.view": {
    kind: "outlook",
    async run(args, call) {
      const from = isoDate(args.start, "start date");
      const to = isoDate(args.end ?? args.start, "end date");
      const start = new Date(`${from}T00:00:00`);
      const end = new Date(`${to}T00:00:00`); end.setDate(end.getDate() + 1);
      const days = (end.getTime() - start.getTime()) / 86_400_000;
      if (days < 1 || days > 31) throw new GraphInputError("Please pick at most 31 days.");
      const q = `startDateTime=${encodeURIComponent(start.toISOString())}&endDateTime=${encodeURIComponent(end.toISOString())}&$select=subject,start,end,isAllDay,showAs,isCancelled,isOnlineMeeting,onlineMeeting,webLink,attendees,organizer,location&$orderby=start/dateTime&$top=150`;
      const r = await call("GET", `/me/calendarView?${q}`, undefined, { Prefer: `outlook.timezone="${tz()}"` });
      return {
        timeZone: tz(),
        events: (r.value ?? []).filter((e: any) => !e.isCancelled).map((e: any) => ({
          id: e.id, subject: e.subject ?? "(no title)", start: e.start?.dateTime, end: e.end?.dateTime, allDay: Boolean(e.isAllDay), showAs: e.showAs,
          teams: Boolean(e.isOnlineMeeting), joinUrl: e.onlineMeeting?.joinUrl ?? null, location: e.location?.displayName ?? "", webLink: e.webLink,
          organizer: person(e.organizer), attendees: (e.attendees ?? []).slice(0, 25).map((a: any) => ({ ...person(a), response: a.status?.response ?? "" })),
        })),
      };
    },
  },
  "meeting.findTimes": {
    kind: "outlook",
    async run(args, call) {
      const who = optEmails(args.attendees);
      const mins = minutes(args.minutes);
      const start = new Date(); start.setMinutes(start.getMinutes() + 30, 0, 0);
      const end = new Date(start); end.setDate(end.getDate() + 7);
      const r = await call("POST", "/me/findMeetingTimes", {
        attendees: who.map((address) => ({ type: "required", emailAddress: { address } })),
        timeConstraint: { activityDomain: "work", timeSlots: [{ start: { dateTime: toLocal(start), timeZone: tz() }, end: { dateTime: toLocal(end), timeZone: tz() } }] },
        meetingDuration: `PT${mins}M`, maxCandidates: 6, isOrganizerOptional: false, returnSuggestionReasons: true,
      }, { Prefer: `outlook.timezone="${tz()}"` });
      return { reason: r.emptySuggestionsReason ?? "", slots: (r.meetingTimeSuggestions ?? []).map((s: any) => ({ start: s.meetingTimeSlot?.start?.dateTime, end: s.meetingTimeSlot?.end?.dateTime, confidence: s.confidence })) };
    },
  },
  "meeting.create": {
    kind: "outlook",
    async run(args, call, confirm) {
      const subject = str(args.subject, "meeting title", 255);
      const start = localDateTime(args.start);
      const mins = minutes(args.minutes);
      const who = optEmails(args.attendees);
      const agenda = optStr(args.agenda, "agenda");
      const location = optStr(args.location, "location", 255);
      const teams = args.teams !== false;
      const endD = new Date(start); endD.setMinutes(endD.getMinutes() + mins);
      if (!(await confirm(teams ? "Book this Teams meeting?" : "Book this Outlook meeting?", `${subject}\n${start.replace("T", " ").slice(0, 16)} for ${mins} minutes${location ? `\nWhere: ${location}` : ""}\nWith: ${who.join(", ") || "just you"}\n\n${who.length ? "Invitations go to everyone listed." : "It goes in your calendar."}`))) return { cancelled: true };
      const e = await call("POST", "/me/events", {
        subject, body: { contentType: "Text", content: agenda },
        start: { dateTime: start, timeZone: tz() }, end: { dateTime: toLocal(endD), timeZone: tz() },
        attendees: who.map((address) => ({ type: "required", emailAddress: { address } })),
        ...(location ? { location: { displayName: location } } : {}),
        ...(teams ? { isOnlineMeeting: true, onlineMeetingProvider: "teamsForBusiness" } : {}),
      });
      return { webLink: e.webLink, joinUrl: e.onlineMeeting?.joinUrl ?? null };
    },
  },
  "meeting.cancel": {
    kind: "outlook",
    async run(args, call, confirm) {
      const eventId = id(args.eventId);
      const note = optStr(args.comment, "message");
      const e = await call("GET", `/me/events/${eventId}?$select=subject,start,end,isOrganizer,attendees`, undefined, { Prefer: `outlook.timezone="${tz()}"` });
      const when = String(e.start?.dateTime ?? "").replace("T", " ").slice(0, 16);
      const organizer = Boolean(e.isOrganizer);
      const others = (e.attendees ?? []).length;
      // Her own meeting is cancelled (everyone is told); someone else's is declined (the organizer is told).
      if (!(await confirm(organizer ? "Cancel this meeting?" : "Decline this meeting?",
        `${e.subject ?? "(no title)"}\n${when}\n\n${organizer ? (others ? "Everyone invited gets a cancellation." : "It's removed from your calendar.") : "The organizer is told you won't come."}${note ? `\nMessage: ${note}` : ""}`))) return { cancelled: true };
      if (organizer) await call("POST", `/me/events/${eventId}/cancel`, { comment: note });
      else await call("POST", `/me/events/${eventId}/decline`, { comment: note, sendResponse: true });
      return { done: organizer ? "cancelled" : "declined" };
    },
  },
  "meeting.move": {
    kind: "outlook",
    async run(args, call, confirm) {
      const eventId = id(args.eventId);
      const start = localDateTime(args.start);
      const e = await call("GET", `/me/events/${eventId}?$select=subject,start,end,isOrganizer,attendees`, undefined, { Prefer: `outlook.timezone="${tz()}"` });
      if (!e.isOrganizer) throw new GraphInputError("Only the organizer can move this meeting. Ask the organizer, or decline it and suggest another time.");
      const oldStart = new Date(String(e.start?.dateTime)), oldEnd = new Date(String(e.end?.dateTime));
      const mins = args.minutes == null ? Math.max(5, Math.round((oldEnd.getTime() - oldStart.getTime()) / 60_000)) : minutes(args.minutes);
      const endD = new Date(start); endD.setMinutes(endD.getMinutes() + mins);
      const others = (e.attendees ?? []).length;
      if (!(await confirm("Move this meeting?", `${e.subject ?? "(no title)"}\nFrom ${String(e.start?.dateTime ?? "").replace("T", " ").slice(0, 16)}\nTo ${start.replace("T", " ").slice(0, 16)} for ${mins} minutes\n\n${others ? "Everyone invited gets the new time." : "It's moved in your calendar."}`))) return { cancelled: true };
      await call("PATCH", `/me/events/${eventId}`, { start: { dateTime: start, timeZone: tz() }, end: { dateTime: toLocal(endD), timeZone: tz() } });
      return { moved: true };
    },
  },
  // ----- Teams -----
  "teams.chats": {
    kind: "teams",
    async run(_args, call) {
      const r = await call("GET", "/me/chats?$expand=lastMessagePreview&$top=12&$orderby=lastMessagePreview/createdDateTime desc");
      return (r.value ?? []).map((c: any) => ({
        id: c.id, topic: c.topic ?? "", type: c.chatType, webUrl: c.webUrl,
        last: c.lastMessagePreview ? { from: c.lastMessagePreview.from?.user?.displayName ?? "", text: decodeHtmlText(String(c.lastMessagePreview.body?.content ?? "")).slice(0, 200), at: c.lastMessagePreview.createdDateTime } : null,
      }));
    },
  },
  "teams.chatMessages": {
    kind: "teams",
    async run(args, call) {
      const r = await call("GET", `/me/chats/${id(args.chatId)}/messages?$top=${Math.min(50, Math.max(1, Number(args.max) || 20))}`);
      return (r.value ?? []).filter((m: any) => m.messageType === "message" && !m.deletedDateTime).map((m: any) => ({
        from: m.from?.user?.displayName ?? m.from?.application?.displayName ?? "", at: localStamp(m.createdDateTime),
        text: decodeHtmlText(String(m.body?.content ?? "")).slice(0, 2000),
      }));
    },
  },
  "teams.joined": {
    kind: "teams",
    async run(_args, call) {
      const r = await call("GET", "/me/joinedTeams?$select=id,displayName,description");
      return (r.value ?? []).map((t: any) => ({ id: t.id, name: t.displayName, description: t.description ?? "" }));
    },
  },
  // ----- Planner -----
  "planner.tasks": {
    kind: "planner",
    async run(_args, call) {
      const r = await call("GET", "/me/planner/tasks");
      const tasks = (r.value ?? []) as any[];
      const planIds = [...new Set(tasks.map((t) => t.planId))].slice(0, 20);
      const titles: Record<string, string> = {};
      await Promise.all(planIds.map(async (p) => { try { titles[p] = (await call("GET", `/planner/plans/${encodeURIComponent(p)}?$select=title`)).title; } catch { titles[p] = "Plan"; } }));
      return tasks.map((t) => ({ id: t.id, title: t.title, planId: t.planId, plan: titles[t.planId] ?? "Plan", percent: t.percentComplete ?? 0, due: t.dueDateTime ?? null, priority: t.priority ?? 5 }));
    },
  },
  "planner.plans": {
    kind: "planner",
    async run(_args, call) {
      // Plans come from two places: the person's own plan list, and every Microsoft 365 group (team) they belong to.
      const out = new Map<string, { id: string; title: string; group: string; groupId: string }>();
      const groups = await myGroups(call);
      const names = new Map(groups.map((g) => [g.id, g.name]));
      try {
        const mine = await call("GET", "/me/planner/plans?$select=id,title,container");
        for (const x of mine.value ?? []) {
          const gid = String(x.container?.containerId ?? "");
          out.set(x.id, { id: x.id, title: x.title, group: names.get(gid) ?? "My plans", groupId: gid });
        }
      } catch { /* not available for every account */ }
      await Promise.all(groups.slice(0, 40).map(async (g) => {
        try { const p = await call("GET", `/groups/${encodeURIComponent(g.id)}/planner/plans?$select=id,title`); for (const x of p.value ?? []) out.set(x.id, { id: x.id, title: x.title, group: g.name, groupId: g.id }); } catch { /* group without plans */ }
      }));
      return [...out.values()].sort((a, b) => a.title.localeCompare(b.title));
    },
  },
  "planner.planTasks": {
    kind: "planner",
    async run(args, call) {
      // Every task in one plan, whoever it's assigned to, with bucket and people's names.
      const planId = encodeURIComponent(str(args.planId, "plan", 100));
      const [plan, buckets, tasks] = await Promise.all([
        call("GET", `/planner/plans/${planId}?$select=id,title,container`),
        call("GET", `/planner/plans/${planId}/buckets?$select=id,name`),
        call("GET", `/planner/plans/${planId}/tasks`),
      ]);
      const groupId = String(plan.container?.containerId ?? "");
      const names = new Map<string, string>();
      if (groupId) {
        try { for (const m of (await call("GET", `/groups/${encodeURIComponent(groupId)}/members?$select=id,displayName&$top=200`)).value ?? []) names.set(m.id, m.displayName ?? ""); } catch { /* names are optional */ }
      }
      const bucketName = new Map<string, string>(((buckets.value ?? []) as any[]).map((b) => [b.id, b.name]));
      return {
        plan: plan.title ?? "Plan",
        tasks: ((tasks.value ?? []) as any[]).map((t) => ({
          id: t.id, title: t.title, planId: t.planId, plan: plan.title ?? "Plan", bucket: bucketName.get(t.bucketId) ?? "",
          percent: t.percentComplete ?? 0, due: t.dueDateTime ?? null, priority: t.priority ?? 5,
          assignees: Object.keys(t.assignments ?? {}).map((u) => names.get(u) || "Someone"),
        })),
      };
    },
  },
  "planner.groups": {
    kind: "planner",
    async run(_args, call) { return myGroups(call); },
  },
  "planner.createPlan": {
    kind: "planner",
    async run(args, call, confirm) {
      const groupId = str(args.groupId, "team", 100);
      const title = str(args.title, "plan name", 100);
      const groupName = optStr(args.groupName, "team", 200) || "your team";
      if (!(await confirm(`Create the plan "${title}"?`, `In: ${groupName}\n\nEveryone in this team can see the plan and its tasks.`))) return { cancelled: true };
      const p = await call("POST", "/planner/plans", { title, container: { url: `https://graph.microsoft.com/v1.0/groups/${encodeURIComponent(groupId)}` } });
      return { id: p.id, title: p.title, group: groupName, groupId };
    },
  },
  // ----- Import from Excel (Send to Planner) -----
  "planner.prepare": {
    kind: "planner",
    async run(args, call) {
      const planId = encodeURIComponent(str(args.planId, "plan", 100));
      const groupId = args.groupId ? encodeURIComponent(str(args.groupId, "group", 100)) : "";
      const [buckets, tasks, details, members] = await Promise.all([
        call("GET", `/planner/plans/${planId}/buckets`),
        call("GET", `/planner/plans/${planId}/tasks?$select=title`),
        call("GET", `/planner/plans/${planId}/details`),
        groupId ? call("GET", `/groups/${groupId}/members?$select=id,displayName,givenName,mail&$top=200`).catch(() => ({ value: [] })) : Promise.resolve({ value: [] }),
      ]);
      return {
        buckets: (buckets.value ?? []).map((b: any) => ({ id: b.id, name: b.name })),
        titles: (tasks.value ?? []).map((t: any) => String(t.title ?? "")),
        labels: details.categoryDescriptions ?? {},
        members: (members.value ?? []).filter((m: any) => m.id).map((m: any) => ({ id: m.id, name: m.displayName ?? "", first: m.givenName ?? String(m.displayName ?? "").split(" ")[0], mail: m.mail ?? "" })),
      };
    },
  },
  "planner.import": {
    kind: "planner",
    async run(args, call, confirm) {
      const planId = str(args.planId, "plan", 100);
      const planTitle = optStr(args.planTitle, "plan", 200) || "your plan";
      if (!Array.isArray(args.tasks) || !args.tasks.length || args.tasks.length > 200) throw new GraphInputError("Choose between 1 and 200 tasks.");
      const DATE = /^\d{4}-\d{2}-\d{2}$/;
      const tasks = (args.tasks as Record<string, unknown>[]).map((t) => {
        const percent = Number(t.percent); const priority = Number(t.priority);
        if (![0, 50, 100].includes(percent) || ![1, 3, 5, 9].includes(priority)) throw new GraphInputError("Please check the task progress and priority.");
        const start = t.start ? str(t.start, "start date", 10) : ""; const due = t.due ? str(t.due, "due date", 10) : "";
        if ((start && !DATE.test(start)) || (due && !DATE.test(due))) throw new GraphInputError("Please check the dates.");
        const labels = Array.isArray(t.labels) ? t.labels.filter((l) => typeof l === "string" && /^category([1-9]|1[0-9]|2[0-5])$/.test(l)) as string[] : [];
        return { key: str(t.key, "row", 40), title: str(t.title, "task", 255), bucket: str(t.bucket, "bucket", 100), start, due, percent, priority, assignee: t.assignee ? str(t.assignee, "person", 100) : "", labels, notes: optStr(t.notes, "notes", 30000) };
      });
      const newLabels = args.labelNames && typeof args.labelNames === "object" ? Object.entries(args.labelNames as Record<string, unknown>).filter(([k, v]) => /^category([1-9]|1[0-9]|2[0-5])$/.test(k) && typeof v === "string" && v.length <= 50) as [string, string][] : [];
      const buckets = [...new Set(tasks.map((t) => t.bucket))];
      const list = tasks.slice(0, 8).map((t) => `• ${t.title}`).join("\n") + (tasks.length > 8 ? `\n…and ${tasks.length - 8} more` : "");
      if (!(await confirm(`Create ${tasks.length} task${tasks.length > 1 ? "s" : ""} in "${planTitle}"?`, `${list}\n\nBuckets: ${buckets.join(", ")}\nAssigned people get a Planner notification.`))) return { cancelled: true };

      const pid = encodeURIComponent(planId);
      // Name labels (only ones that are still unnamed in this plan).
      if (newLabels.length) {
        try {
          const det = await call("GET", `/planner/plans/${pid}/details`);
          const desc: Record<string, string> = {};
          for (const [k, v] of newLabels) if (!det.categoryDescriptions?.[k]) desc[k] = v;
          if (Object.keys(desc).length) await call("PATCH", `/planner/plans/${pid}/details`, { categoryDescriptions: desc }, { "If-Match": det["@odata.etag"] });
        } catch { /* labels are a nicety */ }
      }
      // Buckets: reuse by name, create the missing ones.
      const existing = await call("GET", `/planner/plans/${pid}/buckets`);
      const byName = new Map<string, string>((existing.value ?? []).map((b: any) => [String(b.name).toLowerCase(), b.id]));
      for (const name of buckets) {
        if (!byName.has(name.toLowerCase())) { const b = await call("POST", "/planner/buckets", { name, planId, orderHint: " !" }); byName.set(name.toLowerCase(), b.id); }
      }
      const results: { key: string; ok: boolean; error?: string }[] = [];
      for (const t of tasks) {
        try {
          const body: Record<string, unknown> = { planId, bucketId: byName.get(t.bucket.toLowerCase()), title: t.title, percentComplete: t.percent, priority: t.priority };
          if (t.start) body.startDateTime = `${t.start}T12:00:00Z`;
          if (t.due) body.dueDateTime = `${t.due}T12:00:00Z`;
          if (t.assignee) body.assignments = { [t.assignee]: { "@odata.type": "#microsoft.graph.plannerAssignment", orderHint: " !" } };
          if (t.labels.length) body.appliedCategories = Object.fromEntries(t.labels.map((l) => [l, true]));
          const created = await call("POST", "/planner/tasks", body);
          if (t.notes) {
            const det = await call("GET", `/planner/tasks/${encodeURIComponent(created.id)}/details`);
            await call("PATCH", `/planner/tasks/${encodeURIComponent(created.id)}/details`, { description: t.notes, previewType: "description" }, { "If-Match": det["@odata.etag"] });
          }
          results.push({ key: t.key, ok: true });
        } catch (e) {
          results.push({ key: t.key, ok: false, error: e instanceof Error ? e.message : "Failed" });
        }
        await new Promise((r) => setTimeout(r, 150)); // be gentle with Planner's limits
      }
      return { results };
    },
  },
  "planner.createTask": {
    kind: "planner",
    async run(args, call, confirm) {
      const planId = str(args.planId, "plan", 100);
      const title = str(args.title, "task", 255);
      const due = args.due ? str(args.due, "due date", 10) : "";
      if (due && !/^\d{4}-\d{2}-\d{2}$/.test(due)) throw new GraphInputError("Please check the due date.");
      const planTitle = optStr(args.planTitle, "plan", 200) || "your plan";
      if (!(await confirm("Create this Planner task?", `${title}\nIn: ${planTitle}${due ? `\nDue: ${due}` : ""}${args.assignToMe ? "\nAssigned to you" : ""}`))) return { cancelled: true };
      const body: Record<string, unknown> = { planId, title };
      if (due) body.dueDateTime = `${due}T17:00:00Z`;
      if (args.assignToMe) { const me = await call("GET", "/me?$select=id"); body.assignments = { [me.id]: { "@odata.type": "#microsoft.graph.plannerAssignment", orderHint: " !" } }; }
      const t = await call("POST", "/planner/tasks", body);
      return { id: t.id };
    },
  },
  // ----- SharePoint and OneDrive -----
  "files.recent": {
    kind: "files",
    async run(_args, call) {
      const r = await call("GET", "/me/drive/recent?$top=20");
      return (r.value ?? []).map((f: any) => ({ id: f.remoteItem?.id ?? f.id, driveId: f.remoteItem?.parentReference?.driveId ?? f.parentReference?.driveId ?? "", name: f.name, webUrl: f.webUrl ?? f.remoteItem?.webUrl, modified: f.lastModifiedDateTime ?? f.remoteItem?.lastModifiedDateTime, size: f.size ?? f.remoteItem?.size ?? 0, where: f.remoteItem?.parentReference?.driveType === "documentLibrary" || f.parentReference?.driveType === "documentLibrary" ? "SharePoint" : "OneDrive", by: f.lastModifiedBy?.user?.displayName ?? f.remoteItem?.lastModifiedBy?.user?.displayName ?? "" }));
    },
  },
  "files.search": {
    kind: "files",
    async run(args, call) {
      const q = str(args.q, "search", 200);
      const r = await call("POST", "/search/query", { requests: [{ entityTypes: ["driveItem"], query: { queryString: q }, from: 0, size: 20 }] });
      const hits = r.value?.[0]?.hitsContainers?.[0]?.hits ?? [];
      return hits.map((h: any) => ({ id: h.resource?.id, driveId: h.resource?.parentReference?.driveId ?? "", name: h.resource?.name, webUrl: h.resource?.webUrl, modified: h.resource?.lastModifiedDateTime, size: h.resource?.size ?? 0, where: /sharepoint\.com\/sites\//.test(h.resource?.webUrl ?? "") ? "SharePoint" : "OneDrive", by: h.resource?.lastModifiedBy?.user?.displayName ?? "", summary: String(h.summary ?? "").replace(/<[^>]+>/g, "") }));
    },
  },
  "files.read": {
    kind: "files",
    async run(args, call) {
      // One SharePoint or OneDrive file, for VERA to read. Downloaded straight from Microsoft with a short-lived link.
      const drive = id(args.driveId);
      // No $select: Graph includes the short-lived @microsoft.graph.downloadUrl only in the full item.
      const item = await call("GET", `/drives/${drive}/items/${id(args.itemId)}`);
      if (item.folder) return { name: item.name, note: "That's a folder, not a file." };
      const size = Number(item.size) || 0;
      if (size > MAX_READ_ATTACHMENT) return { name: item.name, size, note: "This file is larger than 15 MB, so VERA can't open it here." };
      const url = String(item["@microsoft.graph.downloadUrl"] ?? "");
      if (!/^https:\/\/[^/]+\.(sharepoint\.com|microsoft\.com|live\.com|1drv\.com|onedrive\.com)\//i.test(url)) return { name: item.name, note: "Microsoft didn't give a download link for this file." };
      const res = await fetch(url);
      if (!res.ok) throw new GraphInputError("Microsoft 365 didn't send the file. Please try again.");
      const bytes = new Uint8Array(await res.arrayBuffer());
      const contentType = String(item.file?.mimeType || "application/octet-stream");
      const isText = /^text\/|\/(json|csv|xml)$/.test(contentType) || /\.(txt|md|csv|json)$/i.test(String(item.name));
      return isText
        ? { name: item.name, contentType, size, webUrl: item.webUrl, text: new TextDecoder().decode(bytes).slice(0, 100_000) }
        : { name: item.name, contentType, size, webUrl: item.webUrl, dataUrl: `data:${contentType};base64,${bytesToBase64(bytes)}` };
    },
  },
};

/** Microsoft 365 groups (teams) the person belongs to: Planner plans live in these. */
async function myGroups(call: Call): Promise<{ id: string; name: string }[]> {
  const out: { id: string; name: string }[] = [];
  let next: string | null = "/me/memberOf?$select=id,displayName,groupTypes&$top=100";
  for (let i = 0; next && i < 5; i++) {
    const r: any = await call("GET", next);
    for (const g of r.value ?? []) if (g["@odata.type"] === "#microsoft.graph.group" && (g.groupTypes ?? []).includes("Unified")) out.push({ id: g.id, name: g.displayName ?? "Team" });
    next = typeof r["@odata.nextLink"] === "string" && r["@odata.nextLink"].startsWith("https://graph.microsoft.com/v1.0/") ? r["@odata.nextLink"].slice("https://graph.microsoft.com/v1.0".length) : null;
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Turns Graph errors into plain words. */
export function friendlyGraphError(status: number, code: string): string {
  if (status === 401) return "Microsoft asked VERA to sign in again. Please reconnect in Connections.";
  if (code === "MailboxNotEnabledForRESTAPI" || code === "ResourceNotFound" && status === 404) return "This account doesn't have an Outlook mailbox. It needs a Microsoft 365 licence with email.";
  if (status === 403) return "Your account doesn't have access to this. It may need a Microsoft 365 licence, or an admin's approval in Entra ID.";
  if (status === 404) return "That item isn't there any more.";
  if (status === 429) return "Microsoft 365 is busy. Please try again in a minute.";
  return "Microsoft 365 didn't answer. Please try again.";
}

export function makeCall(token: string): Call {
  return async (method, path, body, headers = {}) => {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}), ...headers },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      let code = "";
      try { code = ((await res.json()) as { error?: { code?: string } }).error?.code ?? ""; } catch { /* ignore */ }
      throw new GraphInputError(friendlyGraphError(res.status, code));
    }
    return res.status === 204 ? {} : res.json();
  };
}
